import React, { cloneElement } from 'react';
import { List, Datagrid, downloadCSV, Filter,
    TopToolbar, CreateButton, ExportButton, Button, sanitizeListRestProps,
    TextField, ReferenceArrayField, ArrayField, DateField,
    EditButton, DeleteButton, Edit, Create, SimpleForm,
    TextInput, ReferenceInput, ReferenceArrayInput, SelectInput, SelectArrayInput,
    SingleFieldList, ChipField, BooleanInput,
    useQuery, Loading, Error, required,
    useTranslate, useRedirect } from 'react-admin';
import jsonExport from 'jsonexport/dist';
import { Box } from '@material-ui/core';
import PeopleIcon from '@material-ui/icons/People';
import ImportIcon from '@material-ui/icons/CloudUpload';
import ExportIcon from '@material-ui/icons/CloudDownload';

import { SectionTitle, Separator, EditToolbar, HistoryAside,
    NameInput,
    validatePasswords } from './ui_components';
import PlayerEmpty from './player_empty';

const PlayerImportButton = props => {
  const redirect = useRedirect();
  return (
    <Button
        onClick={() => {
          redirect('/student_files/create');
         }}
        label="ra.action.import"
    >
      <ImportIcon />
    </Button>
  );
}

const PlayerFilter = props => (
  <Filter {...props}>
    <ReferenceInput source="lesson_groups" reference="lesson_groups" alwaysOn allowEmpty
        label="resources.players.fields.lesson_group_ids" >
      <SelectInput source="name" />
    </ReferenceInput>
    <TextInput source="student_id" allowEmpty />
    <TextInput source="grade" allowEmpty />
    <TextInput source="school_class" allowEmpty />
    <TextInput source="last_name" allowEmpty />
    <TextInput source="first_name" allowEmpty />
    <BooleanInput source="show_deleted" />
  </Filter>
);

const PlayerListActions  = ({
  currentSort,
  className,
  resource,
  filters,
  displayedFilters,
  exporter, // you can hide ExportButton if exporter = (null || false)
  filterValues,
  permanentFilter,
  hasCreate, // you can hide CreateButton if hasCreate = false
  basePath,
  selectedIds,
  onUnselectItems,
  showFilter,
  maxResults,
  total,
  ...rest
}) => (
  <TopToolbar className={className} {...sanitizeListRestProps(rest)}>
    {filters && cloneElement(filters, {
      resource,
      showFilter,
      displayedFilters,
      filterValues,
      context: 'button',
    })}
    {hasCreate && <CreateButton basePath={basePath} />}
    <PlayerImportButton />
    <ExportButton
      disabled={total === 0}
      resource={resource}
      sort={currentSort}
      filter={{ ...filterValues, ...permanentFilter }}
      exporter={exporter}
      maxResults={maxResults}
      icon={<ExportIcon />}
    />
    {/* Add your custom actions */}
  </TopToolbar>
);

PlayerListActions.defaultProps = {
  selectedIds: [],
  onUnselectItems: () => null,
};

const exporter = translate => {
  const fields = ['student_id', 'last_name', 'first_name',
    'grade', 'school_class', 'lesson_group_names',
    'login_id', 'initial_password', 'cleared_chapters'];
  const headers = fields.map(h => {
    return translate('resources.players.fields.' + h)
  })

  return list => {
    const exportList = list.map(elm => {
      const exportAttrs = {};
      fields.forEach((k, i) => {
        exportAttrs[headers[i]] = (k === 'cleared_chapters')
          ? elm[k].map(e => e.number).join('|')
          : elm[k];
      });
      return exportAttrs;
    });
    jsonExport(exportList, {
        headers: headers // order fields in the export
    }, (err, csv) => {
        downloadCSV(csv, 'students'); // download as 'posts.csv` file
    });
  };
};

const PlayerDeleteButton = props => {
  const translate = useTranslate();
  const confirmTitle = translate('resources.messages.delete_title', { name: `${props.record.name}` });
  return !props.record.deleted_at && <DeleteButton {...props} undoable={false} confirmTitle={confirmTitle} />
}

const PlayerList = props => {
  const translate = useTranslate();

  const { data, loading, error } = useQuery({ 
    type: 'getOne',
    resource: 'profiles',
    payload: { id: 0 }
  });

  if (loading) return <Loading />;
  if (error || !data) return <Error />;

  return (<List filters={<PlayerFilter />} {...props} actions={<PlayerListActions />}
      exporter={exporter(translate)} empty={<PlayerEmpty import={<PlayerImportButton />} />}
      hasCreate={data.player_creatable} bulkActionButtons={false} >
    <Datagrid>
      <TextField source="student_id" />
      <TextField source="login_id" />
      <TextField source="last_name" />
      <TextField source="first_name" />
      <TextField source="grade" />
      <TextField source="school_class" />
      <ArrayField source="cleared_chapters">
        <SingleFieldList>
          <ChipField source="number" />
        </SingleFieldList>
      </ArrayField>
      <ReferenceArrayField source="lesson_group_ids" reference="lesson_groups" sortable={false}>
        <SingleFieldList>
            <ChipField source="name" />
        </SingleFieldList>
      </ReferenceArrayField>
      <DateField source="last_login" label="resources.labels.last_login" locales="ja-JP"
        options={{
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit',
          hour12: false,
          timeZone: 'Asia/Tokyo' 
        }} />
      <EditButton />
      <PlayerDeleteButton />
    </Datagrid>
  </List>)
};

export const GradeClassInput = props => {
  const translate = useTranslate();
  const inputProps = {
    validate: [required()],
    style: { width: '100%' },
    ...props
  }

  return (
    <Box display={{ xs: 'block', sm: 'flex' }} style={{ width: '100%' }}>
      <Box flex={1} mr={{ xs: 0, sm: '0.5em' }}>
        <TextInput source="grade"
          label={translate('resources.players.fields.grade')}
          {...inputProps}
        />
      </Box>
      <Box flex={2} mr={{ xs: 0, sm: '0.5em' }}>
        <TextInput source="school_class"
          label={translate('resources.players.fields.school_class')}
          {...inputProps}
        />
      </Box>
    </Box>
  );
};

const PlayerEditForm = props => {
  const deleted = props.record && props.record.deleted_at ? true : false;
  const disabled= deleted ? {disabled: true} : {}
  return (
    <SimpleForm {...props}>
      <SectionTitle label="resources.labels.profile" />
      <NameInput {...disabled} />
      <GradeClassInput {...disabled} />
      <TextInput source="student_id" {...disabled} validate={[required()]} style={{ width: '100%' }}/>
      <Separator />
      <SectionTitle label="resources.labels.management" />
      <TextField source="login_id" />
      <ReferenceArrayInput source="lesson_group_ids_assigned" {...disabled} reference="lesson_groups" style={{ width: '100%' }}>
        <SelectArrayInput optionText="name" validate={[required()]} />
      </ReferenceArrayInput>
      <Separator />
      <SectionTitle label="resources.labels.password" />
      <BooleanInput source="password_reset" />
      <TextField source="initial_password" />
      {deleted && <Separator />}
      {deleted && <BooleanInput source="restore" />}
    </SimpleForm>
  );
};

const PlayerEdit = props => (
  <Edit {...props} undoable={false} aside={<HistoryAside />}>
    <PlayerEditForm validate={validatePasswords} toolbar={<EditToolbar />} />
  </Edit>
);

const PlayerCreate = props => (
  <Create {...props}>
    <SimpleForm redirect='list'>
      <SectionTitle label="resources.labels.profile" />
      <NameInput />
      <GradeClassInput />
      <TextInput source="student_id" validate={[required()]} style={{ width: '100%' }}/>
      <Separator />
      <SectionTitle label="resources.labels.management" />
      <ReferenceArrayInput source="lesson_group_ids" reference="lesson_groups" style={{ width: '100%' }}>
        <SelectArrayInput optionText="name" validate={[required()]} />
      </ReferenceArrayInput>
    </SimpleForm>
  </Create>
);

export default {
  list: PlayerList,
  create: PlayerCreate,
  edit: PlayerEdit,
  icon: PeopleIcon,
};
